import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabase';

const AddGroup = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [formData, setFormData] = useState({
    groupName: '',
    degreeProgramme: '',
    selectedDegreeId: '', // Store the degree_details ID
    year: '',
    semester: '',
    academicYear: '',
    status: 'Active'
  });

  const [degreePrograms, setDegreePrograms] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load degree programs from Supabase based on admin's university
  useEffect(() => {
    const loadDegreePrograms = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);

        // Get admin's university_id
        let universityId = currentUser?.university_id;
        
        if (!universityId && currentUser?.id) {
          const { data: adminData, error: adminError } = await supabase
            .from('admins')
            .select('university_id')
            .eq('user_id', currentUser.id)
            .single();
          
          if (!adminError && adminData) {
            universityId = adminData.university_id;
          }
        }

        if (!universityId) {
          console.error('Unable to determine university ID');
          return;
        }

        console.log('Loading degree programs for university:', universityId);

        // Load degree programs from Supabase filtered by university_id
        const { data: degreeData, error: degreeError } = await supabase
          .from('degree_details')
          .select('id, degree_programme, local_international, degree_level, active')
          .eq('university_id', universityId)
          .eq('active', true)
          .order('degree_programme', { ascending: true });

        if (degreeError) {
          console.error('Error loading degree programs:', degreeError);
          throw degreeError;
        }

        // Format degree programs for display: "degree_programme (local_international)"
        const formattedDegreePrograms = degreeData?.map(degree => ({
          id: degree.id,
          displayName: `${degree.degree_programme} (${degree.local_international})`,
          originalData: degree
        })) || [];

        console.log(`Loaded ${formattedDegreePrograms.length} degree programs for university ${universityId}`);
        setDegreePrograms(formattedDegreePrograms);
        
      } catch (error) {
        console.error('Error loading degree programs:', error);
        alert('Error loading degree programs: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      loadDegreePrograms();
    }
  }, [currentUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Handle degree program selection - store both display name and ID
    if (name === 'degreeProgramme') {
      const selectedDegree = degreePrograms.find(degree => degree.displayName === value);
      setFormData(prev => ({
        ...prev,
        selectedDegreeId: selectedDegree ? selectedDegree.id : '',
        degreeProgramme: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.groupName) {
      alert('Group Name is required');
      return;
    }

    if (!formData.selectedDegreeId) {
      alert('Degree Programme is required');
      return;
    }

    if (!formData.year) {
      alert('Year is required');
      return;
    }

    if (!formData.semester) {
      alert('Semester is required');
      return;
    }

    if (!formData.academicYear) {
      alert('Academic Year is required');
      return;
    }

    if (!currentUser) {
      alert('Please log in to add groups');
      return;
    }

    try {
      setLoading(true);

      // Get admin's university_id
      let universityId = currentUser?.university_id;
      
      if (!universityId && currentUser?.id) {
        const { data: adminData, error: adminError } = await supabase
          .from('admins')
          .select('university_id')
          .eq('user_id', currentUser.id)
          .single();
        
        if (!adminError && adminData) {
          universityId = adminData.university_id;
        }
      }

      if (!universityId) {
        alert('Unable to determine your university. Please contact support.');
        return;
      }

      console.log('Adding group for university:', universityId);

      // Prepare group data for Supabase groups table
      const groupData = {
        group_name: formData.groupName,                     // Group Name -> group_name
        year: parseInt(formData.year),                      // Year -> year (integer)
        semester: parseInt(formData.semester),              // Semester -> semester (integer)
        academic_year: formData.academicYear,               // Academic Year -> academic_year
        active: formData.status === 'Active',              // Status -> active (boolean)
        degree_details_id: formData.selectedDegreeId,      // Selected degree ID -> degree_details_id (foreign key)
        university_id: universityId,                       // Admin's university_id
        created_at: new Date().toISOString()
      };

      console.log('Group data being submitted to Supabase:', groupData);

      // Insert group into Supabase
      const { data: groupResult, error: groupError } = await supabase
        .from('groups')
        .insert([groupData])
        .select()
        .single();

      if (groupError) {
        console.error('Error inserting group:', groupError);
        console.error('Full error details:', JSON.stringify(groupError, null, 2));
        throw groupError;
      }

      console.log('Group created successfully:', groupResult);
      alert('Group added successfully!');
      navigate('/academic-management');

    } catch (error) {
      console.error('Error adding group:', error);
      let errorMessage = 'Error adding group: ';
      
      if (error.message) {
        errorMessage += error.message;
      } else if (error.details) {
        errorMessage += error.details;
      } else {
        errorMessage += 'Unknown error occurred';
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div style={{width: '100%', height: '100%', position: 'relative', background: 'white'}}>
      <Sidebar />
      
      <div style={{marginLeft: 290, width: 'calc(100% - 290px)', minHeight: '100vh'}}>
        <Header />
        
        {!currentUser ? (
          <div style={{margin: '40px', textAlign: 'center', padding: '40px'}}>
            <h2 style={{color: '#F04915', fontSize: 24, fontFamily: 'Outfit', fontWeight: '700'}}>
              Please Log In
            </h2>
            <p style={{fontSize: 16, fontFamily: 'Outfit', color: '#77838F'}}>
              You need to be logged in as an admin to add groups.
            </p>
          </div>
        ) : (
        <>
        {/* Page Title */}
        <h1 style={{fontSize: 24, fontFamily: 'Outfit', fontWeight: '700', marginTop: '20px', paddingLeft: '40px'}}>
          Add Group
        </h1>

        {/* Add Group Content Area */}
        <form onSubmit={handleSubmit}>
          {loading && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}>
              <div style={{
                background: 'white',
                padding: '20px 40px',
                borderRadius: 8,
                textAlign: 'center',
                fontSize: 16,
                fontFamily: 'Outfit',
                color: '#F04915'
              }}>
                {degreePrograms.length === 0 ? 'Loading degree programs...' : 'Adding group...'}
              </div>
            </div>
          )}
          <div style={{marginTop: '20px', margin: '20px 40px'}}>
            <div style={{background: '#F04915', padding: '20px', borderTopLeftRadius: 15, borderTopRightRadius: 15}}>
              <h2 style={{color: 'white', fontSize: 24, fontFamily: 'Outfit', fontWeight: '700', margin: 0}}>
                Group Details
              </h2>
            </div>

            <div style={{
              background: '#FFFBF5', 
              padding: '40px',
              borderBottomLeftRadius: 15,
              borderBottomRightRadius: 15
            }}>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
                {/* Left Column */}
                <div>
                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Group Name <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <input
                      name="groupName"
                      type="text"
                      placeholder="SE01"
                      value={formData.groupName}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Degree Programme <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <select
                      name="degreeProgramme"
                      value={formData.degreeProgramme}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    >
                      <option value="">Select Degree Programme</option>
                      {degreePrograms.map(program => (
                        <option key={program.id} value={program.displayName}>
                          {program.displayName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Year <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <select
                      name="year"
                      value={formData.year}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    >
                      <option value="">Select Year</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                    </select>
                  </div>
                </div>

                {/* Right Column */}
                <div>
                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Semester <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <select
                      name="semester"
                      value={formData.semester}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    >
                      <option value="">Select Semester</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                    </select>
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Academic Year <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <select
                      name="academicYear"
                      value={formData.academicYear}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    >
                      <option value="">Select Academic Year</option>
                      <option value="2023-2024">2023-2024</option>
                      <option value="2024-2025">2024-2025</option>
                      <option value="2025-2026">2025-2026</option>
                      <option value="2026-2027">2026-2027</option>
                      <option value="2027-2028">2027-2028</option>
                    </select>
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Status <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{display: 'flex', justifyContent: 'space-between', margin: '40px'}}>
            <Link to="/academic-management">
              <button 
                type="button" 
                style={{
                  width: 269, 
                  padding: '11px 20px', 
                  background: '#6C757D', 
                  border: 'none', 
                  borderRadius: 8, 
                  color: 'white', 
                  fontSize: 16, 
                  fontFamily: 'Outfit', 
                  fontWeight: '500', 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#5A6268';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#6C757D';
                }}
              >
                Back to Academic Management
              </button>
            </Link>
            <button 
              type="submit" 
              style={{
                width: 269, 
                padding: '11px 20px', 
                background: '#F04915', 
                border: 'none', 
                borderRadius: 8, 
                color: 'white', 
                fontSize: 16, 
                fontFamily: 'Outfit', 
                fontWeight: '500', 
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#D63A0F';
              }}
              onMouseOut={(e) => {
                e.target.style.background = '#F04915';
              }}
            >
              Submit
            </button>
          </div>
        </form>
        </>
        )}
      </div>
    </div>
  );
};

export default AddGroup;
